import React, { FC, memo } from 'react';
import {
  IonGrid,
  IonRow,
  IonCol,
} from '@ionic/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import EmptyState from '../EmptyState';

interface IncidentDataSummaryProps {
  monthly_events?: unknown;
  breakdown_30?: unknown;
}

interface MonthlyDatum { name: string; Incidents: number }
interface BreakdownDatum { name: string; count: number }

// Accepts `monthly_events` and `breakdown_30` in tuple or object form and
// normalizes both defensively before rendering charts.
const IncidentDataSummary: FC<IncidentDataSummaryProps> = ({ monthly_events, breakdown_30 }) => {
  const rawMonthly = Array.isArray(monthly_events) ? monthly_events : [];
  const monthly: MonthlyDatum[] = rawMonthly.reduce<MonthlyDatum[]>((acc, entry) => {
    if (Array.isArray(entry) && typeof entry[0] === 'string' && typeof entry[1] === 'number') {
      acc.push({ name: entry[0], Incidents: entry[1] });
    } else if (
      entry &&
      typeof (entry as any).name === 'string' &&
      typeof (entry as any).Incidents === 'number'
    ) {
      acc.push({ name: (entry as any).name, Incidents: (entry as any).Incidents });
    }
    return acc;
  }, []);

  const rawBreakdown = Array.isArray(breakdown_30) ? breakdown_30 : [];
  const byType: BreakdownDatum[] = rawBreakdown.reduce<BreakdownDatum[]>((acc, item) => {
    if (item && typeof (item as any).name === 'string') {
      const count = typeof (item as any).count === 'number'
        ? (item as any).count
        : typeof (item as any).Incidents === 'number'
          ? (item as any).Incidents
          : null;
      if (count !== null) acc.push({ name: (item as any).name, count });
    }
    return acc;
  }, []);

  if (monthly.length === 0 && byType.length === 0) {
    return <EmptyState message='No incident data for this period' />;
  }

  return (
    <>
      {monthly.length > 0 && (
        <IonGrid style={{ overflowX: 'auto', display: 'block' }}>
          <IonRow>
            <IonCol>
              <h5 style={{ color: '#4197A9', fontSize: 14 }}>Monthly Security Events</h5>
            </IonCol>
          </IonRow>
          <IonRow>
            <ResponsiveContainer width='100%' height={220}>
              <BarChart data={monthly} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='name' />
                <YAxis />
                <Tooltip />
                <Bar dataKey='Incidents' fill='#2596be' />
              </BarChart>
            </ResponsiveContainer>
          </IonRow>
        </IonGrid>
      )}

      {byType.length > 0 && (
        <IonGrid style={{ marginTop: '1em' }}>
          <IonRow>
            <IonCol>
              <h5 style={{ color: '#4197A9', fontSize: 14 }}>30 Day Breakdown by Type</h5>
            </IonCol>
          </IonRow>
          <IonRow>
            <ResponsiveContainer width='100%' height={220}>
              <BarChart data={byType} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray='3 3' />
                <XAxis dataKey='name' />
                <YAxis />
                <Tooltip />
                <Bar dataKey='count' fill='#186c4c' />
              </BarChart>
            </ResponsiveContainer>
          </IonRow>
        </IonGrid>
      )}
    </>
  );
};

export default memo(IncidentDataSummary);
